import * as stylex from "@stylexjs/stylex";
import { format } from "d3-format";
import { defineMessage, useIntl } from "react-intl";
import { withDataLoader } from "../components/data-loader";
import { EmbedChart, StaticChart } from "../components/figure";
import { HeroControlsWrapper } from "../components/hero-controls/hero-controls";
import { Area } from "../components/hero-vis/area";
import { Hero } from "../components/hero-vis/hero";
import MultipleHeroes from "../components/multiple-heroes";
import { metadata } from "../data/data_equity_hero";
import { datasets } from "../data/data_equity_hero_datasets";
import { mkGemEntityDecoder, useCountryEntities, useNamedEntities } from "../domain";
import { HeroStateProvider } from "../domain/hero-state";
import { useContentRect } from "../hooks";
import { layout } from "../materials/layout.stylex";
import { io } from "../prelude";
export * from "../data/data_equity_hero";
const formatValue = format(".2f");
const formatAxisValue = format(".2f");
const targetTitle = defineMessage({
    id: "fig.equity.HERO.targetTitle",
    defaultMessage: "2030 Target"
});
const targetLabel = defineMessage({
    id: "fig.equity.HERO.target",
    defaultMessage: "1.0 wealth parity index for upper secondary school completion"
});
const infoLabel = defineMessage({
    id: "fig.equity.HERO.info",
    defaultMessage: "1.0 wealth parity index for upper secondary school completion"
});
export const Data = mkGemEntityDecoder([
    "ind_id",
    "year"
], {
    value: io.number
});
export const Chart = ({ data: dataRaw, width, csv, xlsx, renderMode, dataset })=>{
    const intl = useIntl();
    const indicatorLabel = intl.formatMessage(defineMessage({
        id: "fig.equity.HERO.indicatorLabel",
        defaultMessage: "Wealth parity index"
    }));
    const tooltipRowLabels = [
        intl.formatMessage(defineMessage({
            id: "fig.equity.HERO.tooltipNotReached",
            defaultMessage: "Parity index"
        })),
        intl.formatMessage(defineMessage({
            id: "fig.equity.HERO.tooltipReached",
            defaultMessage: "Parity index"
        }))
    ];
    const data = useNamedEntities(dataRaw);
    const countryData = useCountryEntities(data);
    return <HeroControlsWrapper renderMode={renderMode} caption={intl.formatMessage(metadata.caption)} content={intl.formatMessage(infoLabel)} source={intl.formatMessage(metadata.source)} id={metadata.id} csv={csv} xlsx={xlsx} dataset={dataset} hideToggle>
      <Area width={width} label={indicatorLabel} values={countryData} tooltipRowLabels={tooltipRowLabels} formatValue={formatValue} formatAxisValue={formatAxisValue}/>
    </HeroControlsWrapper>;
};
const csv = new URL("../data/data_equity_hero.zip", import.meta.url).href;
const xlsx = new URL("../data/data_equity_hero.xlsx", import.meta.url).href;
const HE = ({ renderMode = "default", dataset, renderMultiple })=>{
    const intl = useIntl();
    const [ref, contentRect] = useContentRect();
    const Renderer = withDataLoader(dataset ? datasets[dataset].url : new URL("../data/data_equity_hero.json", import.meta.url).href, Data, (props)=>contentRect.width === 0 ? null : dataset ? <Chart {...props} width={contentRect.width} csv={datasets[dataset].csv} xlsx={datasets[dataset].xlsx} dataset={dataset}/> : <Chart {...props} width={contentRect.width} csv={csv} xlsx={xlsx} dataset={dataset}/>, {
        narrow: 400,
        wide: 400
    });
    const children = <div ref={ref} {...stylex.props(layout.fullWidthHidden)}>
      <Renderer key="equity" renderMode={renderMode}/>
    </div>;
    return renderMode === "embed" ? <EmbedChart caption={intl.formatMessage(infoLabel)} source={intl.formatMessage(metadata.source)} isHeroChart>
      <HeroStateProvider initial={"today"}>{children}</HeroStateProvider>
    </EmbedChart> : renderMode === "static" ? <StaticChart layout={"columns"} title={metadata.title ? intl.formatMessage(metadata.title) : undefined} caption={intl.formatMessage(metadata.caption)} source={intl.formatMessage(metadata.source)}>
      <HeroStateProvider initial={"today"}>{children}</HeroStateProvider>
    </StaticChart> : renderMultiple ? <MultipleHeroes datasets={datasets} Component={HE}/> : <HeroStateProvider>
      <Hero title={metadata.title ? intl.formatMessage(metadata.title) : undefined} caption={intl.formatMessage(metadata.caption)} targetTitle={intl.formatMessage(targetTitle)} targetLabel={intl.formatMessage(targetLabel)}>
        {children}
      </Hero>
    </HeroStateProvider>;
};
export default HE;
